import { REGEX_CLEAN_STRING, REGEX_PASSWORD_UPPERCASE, REGEX_PASSWORD_LOWERCASE, REGEX_PASSWORD_SPECIAL, REGEX_BASE64_PLUS, REGEX_BASE64_SLASH, REGEX_BASE64_PADDING, REGEX_BASE64URL_DASH, REGEX_BASE64URL_UNDERSCORE } from '../constants/regex';

import DOMPurify from 'dompurify';

/**
 * A helper containing various helper methods to support
 * the Vue components.
 */
export class GeneralHelper {
	private regex_email = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
	private regex_slack = new RegExp('https://hooks.slack.com/services/T[a-zA-Z0-9_]{8,14}/B[a-zA-Z0-9_]{8,14}/[a-zA-Z0-9_]{24}');
	private regex_teams_workflow = /^(https?:\/\/)?([\w-]+\.)+[\w]{2,}(:\d{2,5})?(\/\S*)?$/i;
	private regex_phone = /^[\s()+-]*([0-9][\s()+-]*){6,20}$/;
	private regex_uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
	private regex_url = /^(http|https):\/\/(((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:)*@)?(((\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5]))|((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.?)(:\d*)?)(\/((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:|@)+(\/(([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:|@)*)*)?)?(\?((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:|@)|[\uE000-\uF8FF]|\/|\?)*)?(#((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:|@)|\/|\?)*)?$/i;

	/**
	 * This method is used to get a list of properties and only include the unique items.
	 *
	 * @param items Array of objects.
	 * @param prop The prop name you want to use.
	 */
	public getUniqueProperties(items: Array<any>, prop: string, humanise = false): Array<string> {
		const props: Array<string> = items.map(trigger => trigger[prop]);
		const uniqueProps: Array<string> = [];
		for (const index in props) {
			if (humanise) props[index] = props[index] === 'api' ? 'API' : this.capitalise(props[index].replace('_', ' '));
			if (!uniqueProps.includes(props[index])) {
				uniqueProps.push(props[index]);
			}
		}
		return uniqueProps;
	}

	/**
	 * This function will take an object and deep clone it to prevent any kind of pass
	 * by reference.
	 *
	 * @param obj Object to clone.
	 */
	public deepClone<TObject = any>(obj: TObject): TObject {
		return JSON.parse(JSON.stringify(obj));
	}

	/**
	 * Taking some information will attempt to do a lookup of an array based on the given
	 * property and value to find a match and return the array index.
	 *
	 * @param list The array of items.
	 * @param property The property to search for.
	 * @param value The value of the property inside of the list to find.
	 */
	public findObjectByProperty(list: Array<any>, property: string, value: any): number {
		for (const index in list) {
			if (list[index][property] === value) {
				return Number.parseInt(index);
			}
		}
		return -1;
	}

	/**
	 * This will attempt to capitalise a string, by capitalising the first letter or if
	 * given the "all" flag, which will then split the string and capitalise each word.
	 *
	 * @param str The string value to capitalise.
	 * @param all Whether to capitalise all words.
	 */
	public capitalise(str: string, all = false): string {
		if (!all) {
			return `${str.substring(0, 1).toUpperCase()}${str.substring(1).toLowerCase()}`;
		} else {
			return str.split(' ').map((part: string) => {
				return `${part.substring(0, 1).toUpperCase()}${part.substring(1).toLowerCase()}`;
			}).join(' ');
		}
	}

	/**
	 * This function takes a string, and converts it to an alphanumeric only string - removing
	 * any special characters.
	 *
	 * @param value The string to clean.
	 */
	public cleanString(value: string): string {
		return value.replace(REGEX_CLEAN_STRING, '').toLowerCase();
	}

	/**
	 * This function takes a string and will clean it from any XSS attacks.
	 *
	 * @param value The string
	 *
	 * @returns The cleaned string.
	 */
	public cleanStringXSS(value: string): string {
		return DOMPurify.sanitize(value);
	}

	/**
	 * This function will accept a version string, built specifically for a code version, and
	 * will clean it to only show the major and minor version.
	 *
	 * @param value The version string.
	 */
	public cleanCodeVersion(value: string): string {
		const parts = value.split('.');
		return `${parts[0]}.${parts[1]}`;
	}

	/**
	 * Get the version colour difference.
	 */
	public getVersionColourDifference(diff: number): string {
		const colourCodes = { green: '#1d963f', amber: '#dec227', red: '#da1a1a' };
		if (diff <= 1) return colourCodes.green;
		if (diff <= 2) return colourCodes.amber;
		return colourCodes.red;
	}

	/**
	 * This functoon accepts a method and will debounce the function based on the given wait
	 * time.
	 *
	 * @param func The function to debounce.
	 * @param wait The time to wait.
	 */
	public debounce(func: any, wait: number, immediate = false): any {
		// eslint-disable-next-line ts/no-this-alias
		const self = this;
		let timeout: any;
		return () => {
			const context = self as any;
			// eslint-disable-next-line prefer-rest-params
			const args = arguments;
			clearTimeout(timeout);
			timeout = setTimeout(() => {
				timeout = null;
				if (!immediate) func.apply(context, args);
			}, wait);
			if (immediate && !timeout) func.apply(context, args);
		};
	}

	/**
	 * This method will take a password, and optionally the retype as well and verify the password
	 * is within the required restrictions.
	 *
	 * @param password The password typed.
	 * @param password_retype The confirmed retype, to check same password.
	 */
	public validatePassword(password: string, password_retype?: string): boolean | string {
		const errorItems: Array<string> = [];
		if (!REGEX_PASSWORD_UPPERCASE.test(password)) errorItems.push('1 uppercase letter');
		if (!REGEX_PASSWORD_LOWERCASE.test(password)) errorItems.push('1 lowercase letter');
		if (password.length < 10) errorItems.push('10 characters minimum');
		if (!REGEX_PASSWORD_SPECIAL.test(password)) errorItems.push('1 special character');
		if (password_retype !== undefined && password !== password_retype) errorItems.push('passwords do not match');
		if (errorItems.length > 0) return `Your password does not meet the following criteria: ${errorItems.join(', ')}.`;
		return false;
	}

	/**
	 * This will check and validate a value against the given type, using the defined regular
	 * expression patterns.
	 *
	 * @param type The type of value to validate (email, slack, teams).
	 * @param value Email address to validate.
	 */
	public validateField(type: 'slack' | 'teams_workflow' | 'email' | 'phone' | 'uuid' | 'url' = 'email', value: string): boolean {
		let regex: RegExp = this.regex_email;
		if (type === 'slack') regex = this.regex_slack;
		if (type === 'teams_workflow') regex = this.regex_teams_workflow;
		if (type === 'phone') regex = this.regex_phone;
		if (type === 'uuid') regex = this.regex_uuid;
		if (type === 'url') regex = this.regex_url;
		return regex.test(value);
	}

	/**
	 * This method takes a number of bytes and turns it into a human readable string for example
	 * 10000 would become 10KB.
	 *
	 * @param size The size in bytes.
	 * @param decimal_places The number of decimal points.
	 * @param use_metric Whether to use metric measurement (1000) instead of binary (1024).
	 * @returns The human readable version of the size.
	 */
	public sizeToHuman(size: number, decimal_places = 2, use_metric = true): string {
		const thresh = use_metric ? 1000 : 1024;
		if (Math.abs(size) < thresh) return `${size} B`;
		const units = use_metric
			? ['KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
			: ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
		let u = -1;
		const r = 10 ** decimal_places;
		do {
			size /= thresh;
			++u;
		} while (Math.round(Math.abs(size) * r) / r >= thresh && u < units.length - 1);
		return `${size.toFixed(decimal_places)} ${units[u]}`;
	}

	/**
	 * Enables or disables save buttons
	 * @param state Enables the save button(s) by default or disables if set to false
	 */
	public enableSave(state = true, container: Document | Element = document): void {
		const btn: HTMLButtonElement | null = container.querySelector('.btn-save');
		if (btn) {
			btn.disabled = !state;
		}
	}

	/**
	 * This function will accept a HTML element, and will calculate it's offset against
	 * the full page window.
	 * @param element The element to calculate.
	 * @returns IHtmlElementOffset
	 */
	public getElementWindowOffset(element: HTMLElement): {
		left: number,
		top: number,
		right: number,
		bottom: number,
		height: number,
		width: number,
	} {
		if (!element) throw new Error('Element not provided for offset calculation.');

		const rect = element.getBoundingClientRect();
		const leftOffset = rect.left + window.scrollX;
		const topOffset = rect.top + window.scrollY;
		return {
			left: leftOffset,
			top: topOffset,
			right: window.innerWidth - rect.width + leftOffset,
			bottom: window.innerHeight - rect.height + topOffset,
			height: rect.height,
			width: rect.width,
		};
	}

	public exportDataTypeBadgeString(params: Record<string, any>): string {
		if (params.application.length > 0 && params.environment.length > 0 && params.exportFileType.length > 0) {
			return `${params.application} / ${params.environment} / ${params.exportFileType}`;
		}
		return '';
	}

	public toBase64Url(str: string): string {
		const utf8Bytes = new TextEncoder().encode(str);
		const binary = Array.from(utf8Bytes, byte => String.fromCharCode(byte))
			.join('');

		return btoa(binary)
			.replace(REGEX_BASE64_PLUS, '-')
			.replace(REGEX_BASE64_SLASH, '_')
			.replace(REGEX_BASE64_PADDING, '');
	}

	public fromBase64Url(base64url: string): string {
		const base64 = base64url.replace(REGEX_BASE64URL_DASH, '+').replace(REGEX_BASE64URL_UNDERSCORE, '/');
		const padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');
		const binary = atob(padded);
		const bytes = new Uint8Array(Array.from(binary, char => char.charCodeAt(0)));
		return new TextDecoder().decode(bytes);
	}

	/**
	 * Format duration from seconds to human readable string
	 *
	 * @param seconds The duration in seconds.
	 * @returns A human readable string representing the duration.
	 */
	public formatDuration(seconds: number): string {
		const total: number = Math.max(0, Math.floor(seconds));

		const hours: number = Math.floor(total / 3600);
		const minutes: number = Math.floor((total % 3600) / 60);
		const secs: number = total % 60;

		const parts: string[] = [];

		if (hours > 0) {
			parts.push(`${hours}h`);
		}

		if (minutes > 0 || hours > 0) {
			parts.push(`${minutes}m`);
		}

		parts.push(`${secs}s`);

		return parts.join(' ');
	}

	/**
	 * Generates a cryptographically random series of letters and numbers.
	 * Optionally, a prefix can be given. This is useful because the starting
	 * random character might be a number, which isn't an acceptable HTML ID.
	 *
	 * @param prefix Optional prefix.
	 * @returns Randomly generated ID.
	 */
	public randomId(prefix = '') {
		const random = window.crypto.randomUUID()
			.split('-')
			.map(hex => Number.parseInt(hex, 16).toString(36))
			.join('');
		return `${prefix}${random}`;
	}
}
